import type { LayerKey } from '@festina-life/api'
import { notEmpty } from '@festina-life/core'
import { RawTextInput } from '@festina-life/ui/forms'
import { Icon, typographyStyles } from '@festina-life/ui/structure'
import cx from 'classnames'
import { type KeyboardEvent, useState } from 'react'

import { useNavigationParams } from '../../system/useNavigationParams'
import { DraftLocationSelector } from '../draftLocationSelectors/DraftLocationSelector'
import { EditorButtonGroup } from '../editorButtonGroup/EditorButtonGroup'
import { HoverEditButton } from '../hoverEditGroup/HoverEditButton'

type Props = {
  label: string
  description: string
  currentValues: string[]
  onSave: (params: {
    createdValue: string
    layer: LayerKey
    block: string
  }) => void
}

export const AddFieldPlaceholderInput = ({
  currentValues,
  label,
  description,
  onSave,
}: Props) => {
  const { layer, block } = useNavigationParams()
  const [selectedLayer, setSelectedLayer] = useState<LayerKey>(layer)
  const [selectedBlock, setSelectedBlock] = useState<string>(block)
  const [currentValue, setCurrentValue] = useState<string>()

  const trimmedValue = currentValue?.trim()
  const isValueInvalid =
    !trimmedValue ||
    currentValues?.includes(trimmedValue) ||
    /\s/.test(trimmedValue)

  const handleEditValue = () => {
    setCurrentValue('')
  }

  const handleCancel = () => {
    setCurrentValue(undefined)
  }

  const handleSave = () => {
    if (!isValueInvalid) {
      onSave({
        createdValue: trimmedValue,
        layer: selectedLayer,
        block: selectedBlock,
      })

      setCurrentValue(undefined)
    }
  }

  const handleKeyDown = ({ key }: KeyboardEvent<HTMLInputElement>) => {
    switch (key) {
      case 'Escape': {
        handleCancel()
        break
      }
      case 'Enter': {
        handleSave()
        break
      }
      default:
        break
    }
  }

  return (
    <div className="group relative flex size-full flex-col justify-center gap-x-2 p-2">
      {notEmpty(currentValue) ? (
        <div className={cx('size-full', typographyStyles())}>
          <div className="grid grid-cols-[1fr_auto] gap-x-2 items-end">
            <RawTextInput
              autoFocus
              value={currentValue}
              onChange={(event) => setCurrentValue(event.target.value)}
              onKeyDown={handleKeyDown}
              isInError={isValueInvalid && !!trimmedValue}
              className="flex-1"
            />
            <DraftLocationSelector
              layer={selectedLayer}
              block={selectedBlock}
              onLayerChange={setSelectedLayer}
              onBlockChange={setSelectedBlock}
            />
          </div>
          <EditorButtonGroup
            onCancel={handleCancel}
            onSave={handleSave}
            className="relative! mt-1 text-right"
          />
        </div>
      ) : (
        <>
          <div>{label}</div>
          <div className="text-sm">{description}</div>
          <div className="invisible absolute bottom-1 right-2 flex flex-row gap-x-2 group-hover:visible">
            <HoverEditButton
              icon={<Icon name="Pencil" />}
              onClick={handleEditValue}
            />
          </div>
        </>
      )}
    </div>
  )
}
